import type { RefreshResponseDto } from '@gty/common';

import { apiUrl } from './config';
import { ApiError, toApiError } from './errors';
import { notifySessionEnded, setAccessToken } from './token-store';

/**
 * SINGLE-FLIGHT REFRESH.
 *
 * This is the load-bearing invariant of the whole auth client, and it is not an
 * optimisation.
 *
 * The server rotates refresh tokens and treats a second presentation of an
 * already-consumed token as theft: it revokes the entire token family and logs
 * the user out (spec §6.4). If five queued requests each 401 and each fire
 * their own `POST /api/auth/refresh`, four of them present a token the first
 * one just consumed — and the user is logged out by their own client.
 *
 * So: at most ONE refresh is ever in flight. Every concurrent caller awaits the
 * same promise and then retries with whatever token that single refresh
 * produced.
 */

let inFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const response = await fetch(apiUrl('/auth/refresh'), {
    method: 'POST',
    // `include` is what actually sends the HttpOnly refresh cookie. Without it
    // the request is anonymous and always 401s.
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  const body = (await response.json()) as RefreshResponseDto;
  if (typeof body.accessToken !== 'string' || body.accessToken.length === 0) {
    throw new ApiError(response.status, 'Refresh returned no access token', null, null);
  }

  setAccessToken(body.accessToken);
  return body.accessToken;
}

/**
 * Refresh the access token, coalescing concurrent callers onto one request.
 *
 * Resolves with the new access token. Rejects (and ends the session) when the
 * refresh cookie is missing, expired, or has been revoked.
 */
export function refreshAccessToken(): Promise<string> {
  if (inFlight !== null) {
    return inFlight;
  }

  const attempt = performRefresh()
    .catch((error: unknown) => {
      // A failed refresh is terminal: there is no second credential to fall
      // back on. Clear state and let the app redirect to /login.
      const reason =
        error instanceof ApiError && error.code === 'FORCE_REAUTH' ? 'FORCE_REAUTH' : 'REFRESH_FAILED';
      notifySessionEnded(reason);
      throw error;
    })
    .finally(() => {
      // Release the slot only after settle, so a LATER 401 (a genuinely new
      // expiry) can start a fresh refresh, while the current batch shares this
      // one.
      inFlight = null;
    });

  inFlight = attempt;
  return attempt;
}

/** True while a refresh is in flight. Exposed for assertions and diagnostics. */
export function isRefreshInFlight(): boolean {
  return inFlight !== null;
}

/** Test seam: drop any in-flight refresh between specs. */
export function __resetRefreshState(): void {
  inFlight = null;
}
